import React from "react";
import { Alert, Space, Statistic, Table, Tag, Tooltip, Typography } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import { formatDuration } from "../constants";

const { Text } = Typography;

/** The per-table outcome of a finished restore. */
const RestoreResult = ({ result }) => {
  if (!result) return null;

  const columns = [
    {
      title: "Table",
      dataIndex: "table",
      key: "table",
      render: (name, row) => (
        <Space size={6}>
          <Text>{name}</Text>
          {row.warning && (
            <Tooltip title={row.warning}>
              <WarningOutlined style={{ color: "#faad14" }} />
            </Tooltip>
          )}
        </Space>
      ),
    },
    { title: "Read", dataIndex: "read", key: "read", align: "right", width: 90 },
    {
      title: "Inserted",
      dataIndex: "inserted",
      key: "inserted",
      align: "right",
      width: 100,
      render: (n) => (n > 0 ? <Tag color="success">{n.toLocaleString()}</Tag> : n),
    },
    {
      title: "Updated",
      dataIndex: "updated",
      key: "updated",
      align: "right",
      width: 100,
      render: (n) => (n > 0 ? <Tag color="processing">{n.toLocaleString()}</Tag> : n),
    },
    {
      title: "Skipped",
      dataIndex: "skipped",
      key: "skipped",
      align: "right",
      width: 100,
    },
    {
      title: "Deleted",
      dataIndex: "deleted",
      key: "deleted",
      align: "right",
      width: 100,
      render: (n) => (n > 0 ? <Tag color="error">{n.toLocaleString()}</Tag> : n),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Alert
        type={result.success ? "success" : "error"}
        showIcon
        message={
          result.success
            ? `Restore finished in ${formatDuration(result.durationMs)}`
            : "Restore failed and was rolled back"
        }
        description={
          result.success
            ? result.safetyBackupId
              ? `A safety backup (#${result.safetyBackupId}) was taken first — restore it to undo this.`
              : "No safety backup was taken for this restore."
            : result.error
        }
      />

      <Space size={32} wrap>
        <Statistic title="Inserted" value={result.totalInserted ?? 0} />
        <Statistic title="Updated" value={result.totalUpdated ?? 0} />
        <Statistic title="Skipped" value={result.totalSkipped ?? 0} />
        <Statistic title="Deleted" value={result.totalDeleted ?? 0} />
      </Space>

      <Table
        size="small"
        rowKey="table"
        columns={columns}
        dataSource={result.tables || []}
        pagination={false}
        scroll={{ y: 300 }}
      />
    </Space>
  );
};

export default RestoreResult;
